import {getFormattedDate} from "../../../utils/timeUtils";
import {getSelectParam} from "../../../utils/converter";

export const FALLBACK_VALUE = "-";
export const AUTO_SAVE_DELAY = 800;
export const AUTO_SAVE_TOAST_ID = "document-header-auto-save";
export const HEADER_COMPACT_CLASS = "document-header-compact";

export const stripHtml = (value) => {
    if (typeof value !== "string") {
        return value;
    }
    return value.replace(/<[^>]*>/g, "").trim();
};

export const formatBooleanLabel = (value, t) => {
    if (value === true) {
        return t("app.common.yes", {defaultValue: "Sì"});
    }
    if (value === false) {
        return t("app.common.no", {defaultValue: "No"});
    }
    return FALLBACK_VALUE;
};

export const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
        return FALLBACK_VALUE;
    }
    if (typeof value === "string" && value.trim() === "") {
        return FALLBACK_VALUE;
    }
    return value;
};

export const formatDate = (value) => {
    if (!value) {
        return FALLBACK_VALUE;
    }
    try {
        return getFormattedDate(value);
    } catch (error) {
        return FALLBACK_VALUE;
    }
};

const normalizeAddressValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "number") {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => normalizeAddressValue(item))
            .filter(Boolean)
            .join(" ");
    }

    if (typeof value === "object") {
        const candidates = [
            value.full_address,
            value.address,
            value.street,
            value.name,
            value.label,
            value.description,
            value.city,
            value.town,
            value.text,
            value.code,
            value.value,
            value.id,
        ];

        for (const candidate of candidates) {
            const resolved = normalizeAddressValue(candidate);
            if (resolved) {
                return resolved;
            }
        }
        return "";
    }

    return "";
};

const resolveAddressField = (...values) => {
    for (const value of values) {
        const resolved = normalizeAddressValue(value);
        if (resolved) {
            return resolved;
        }
    }
    return "";
};

export const buildAddress = (source) => {
    if (!source) {
        return null;
    }

    if (typeof source === "string") {
        return source;
    }

    const street = resolveAddressField(source.street, source.address, source.address_line1);
    const street2 = resolveAddressField(source.address_line2, source.apartment);
    const zip = resolveAddressField(source.zip, source.zip_code, source.postal_code);
    const city = resolveAddressField(source.city, source.city_name, source.town);
    const province = resolveAddressField(source.province, source.state, source.region);
    const country = resolveAddressField(source.country?.name, source.country?.code, source.country);

    const firstRow = [street, street2].filter(Boolean).join(" ");
    const secondRow = [zip, city].filter(Boolean).join(" ");
    const parts = [firstRow, secondRow, province, country].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : null;
};

export const normalizeAddressOption = (address) => {
    if (!address || typeof address !== "object") {
        return null;
    }

    const resolvedId = address.id ?? address.value ?? null;
    const resolvedLabel = address.full_address
        ?? address.label
        ?? buildAddress(address)
        ?? null;

    if (!resolvedId || !resolvedLabel) {
        return null;
    }

    return {
        id: resolvedId,
        value: resolvedId,
        label: resolvedLabel,
        data: address,
    };
};

export const mergeDocumentData = (current, patch) => {
    const safeCurrent = current ?? {};
    const next = {...safeCurrent};

    Object.entries(patch ?? {}).forEach(([key, value]) => {
        if (key === "currency") {
            if (value === null) {
                next.currency = null;
            } else if (typeof value === "object" && value !== null) {
                const currentCurrency = typeof safeCurrent.currency === "object" && safeCurrent.currency !== null
                    ? safeCurrent.currency
                    : {};
                next.currency = {
                    ...currentCurrency,
                    ...value,
                };
            } else if (typeof safeCurrent.currency === "object" && safeCurrent.currency !== null) {
                next.currency = {
                    ...safeCurrent.currency,
                    code: value,
                };
            } else {
                next.currency = value;
            }
            return;
        }

        next[key] = value;
    });

    return next;
};

export const sanitizeFormValues = (values) => Object.keys(values ?? {}).reduce((accumulator, key) => {
    const value = values[key];

    if (value === "") {
        accumulator[key] = null;
        return accumulator;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        accumulator[key] = trimmed === "" ? null : trimmed;
        return accumulator;
    }

    if (typeof value === "number" && Number.isNaN(value)) {
        accumulator[key] = null;
        return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
}, {});

export const toSelectValue = (entity, label) => {
    if (!entity) {
        return null;
    }
    const value = getSelectParam(entity, label);
    if (!value || Object.keys(value).length === 0) {
        return null;
    }
    return value;
};

export const toPaymentTermOption = (entity) => {
    if (!entity || typeof entity !== "object") {
        return null;
    }
    const id = entity.id ?? entity.value ?? null;
    const label = entity.name ?? entity.label ?? entity.code ?? null;
    if (!id || !label) {
        return null;
    }
    return {
        id,
        value: id,
        label,
        code: entity.code ?? null,
        data: entity,
    };
};

export const toVatOption = (entity) => {
    if (!entity || typeof entity !== "object") {
        return null;
    }
    const id = entity.id ?? entity.value ?? null;
    const label = entity.name ?? entity.label ?? entity.code ?? (entity.value !== undefined ? `${entity.value}%` : null);
    if (!id || !label) {
        return null;
    }
    return {
        id,
        value: id,
        label,
        code: entity.code ?? null,
        rate: entity.value ?? null,
        data: entity,
    };
};

export const toWarehouseOption = (entity) => {
    if (!entity || typeof entity !== "object") {
        return null;
    }
    const id = entity.id ?? entity.value ?? null;
    const label = entity.name ?? entity.label ?? null;
    if (!id || !label) {
        return null;
    }
    return {
        id,
        value: id,
        label,
        name: entity.name ?? label,
        data: entity.data ?? entity,
    };
};

export const sanitizePaymentConditions = (conditions) => (
    (conditions ?? [])
        .map((condition) => ({
            id: condition?.id ?? null,
            percent: Number(condition?.percent ?? 0),
            days: Number(condition?.days ?? 0),
            unit: condition?.unit ?? "after_invoice_date",
        }))
        .filter((condition) => Number.isFinite(condition.percent) && Number.isFinite(condition.days))
        .map((condition) => {
            const payload = {
                percent: condition.percent,
                days: condition.days,
                unit: condition.unit,
            };
            if (condition.id) {
                payload.id = condition.id;
            }
            return payload;
        })
);

export const buildDocumentFormDefaults = (document) => ({
    registration_date: document?.registration_date ?? null,
    document_date: document?.document_date ?? null,
    expire_date: document?.expire_date ?? null,
    next_reminder: document?.next_reminder ?? null,
    document_note: document?.document_note ?? "",
    internal_note: document?.internal_note ?? "",
    connection_note: document?.connection_note ?? "",
    terms_type: toSelectValue(document?.terms_type, "name"),
    company_warehouse: toWarehouseOption(
        document?.company_warehouse
        ?? document?.company_document?.company_warehouse
        ?? null
    ),
});

export const buildPaymentFormDefaults = (document) => ({
    currency: toSelectValue(document?.currency, "code"),
    exchange_date: document?.exchange_date ?? null,
    exchange_rate: document?.exchange_rate ?? null,
    payment_term: toPaymentTermOption(
        document?.company_payment_terms
        ?? document?.company_payment_term
        ?? document?.payment_term
        ?? null
    ),
    company_vat: toVatOption(typeof document?.company_vat === "object" ? document?.company_vat : null),
});

export const buildDeliveryFormDefaults = (document) => ({
    delivery_date: document?.delivery_date ?? null,
    connection_note: document?.connection_note ?? "",
    connection_can_view: Boolean(document?.connection_can_view),
    destination_address: normalizeAddressOption(document?.connection_address ?? document?.destination),
    delivery_address: normalizeAddressOption(document?.delivery_address ?? document?.delivery),
    change_body_delivery_date: false,
});
